import { useMemo } from 'react'

import { formatDecimal, formatInt, formatPct, pctOf } from '../../lib/format.ts'
import type { FleetPayload } from '../../lib/sse/types.ts'
import { VersionBar, VersionLegend } from '../charts/VersionBar.tsx'
import { Metric } from '../primitives/Metric.tsx'
import { Panel } from '../primitives/Panel.tsx'
import { ReferenceStamp } from '../primitives/ReferenceStamp.tsx'

/** Above this, reconnects stop being churn and start being a symptom. */
const RECONNECT_WARN = 8
const RECONNECT_CRIT = 20

/** A day's enrolments this far above the running normal is worth a look. */
const ENROLLMENT_WATCH = 120

/**
 * Fleet operations.
 *
 * Version spread is asked as a risk question, not an inventory one: the figure
 * that leads is how much of the fleet sits on a build with a known issue.
 * Enrolments and reconnect rate sit beside it because both have a security
 * reading as well as an operational one — a burst of enrolments can be an
 * enrolment token being abused, not a rollout.
 */
export function FleetOpsPanel({
  fleet,
  lastFleetAt,
  className,
}: {
  fleet: FleetPayload | null
  lastFleetAt: number | null
  className?: string
}) {
  const versions = fleet?.agent_versions ?? []

  const { affected, total } = useMemo(() => {
    let sum = 0
    let issues = 0
    for (const version of versions) {
      sum += version.count
      if (version.has_known_issue) issues += version.count
    }
    return { affected: issues, total: sum }
  }, [versions])

  const reconnect = fleet?.reconnect_rate_per_min ?? null
  const reconnectTone =
    reconnect === null
      ? 'neutral'
      : reconnect >= RECONNECT_CRIT
        ? 'crit'
        : reconnect >= RECONNECT_WARN
          ? 'warn'
          : 'neutral'

  const enrollments = fleet?.enrollments_last_24h ?? null

  return (
    <Panel
      title="Frota — operação"
      className={className}
      meta={<ReferenceStamp fleet={fleet} lastFleetAt={lastFleetAt} />}
    >
      <div className="flex h-full flex-col gap-3">
        <div>
          <div className="mb-1.5 flex items-end justify-between gap-2">
            <Metric
              label="em versão com issue conhecida"
              value={formatInt(affected)}
              size="lg"
              tone={affected > 0 ? 'warn' : 'ok'}
              note={
                total > 0 ? (
                  <>
                    <span className="tnum">{formatPct(pctOf(affected, total), 1)}</span> da frota
                  </>
                ) : undefined
              }
            />
          </div>
          <VersionBar versions={versions} />
          <VersionLegend versions={versions} />
        </div>

        <div className="mt-auto grid grid-cols-2 gap-3 border-t border-line-subtle pt-3">
          <div>
            <span className="text-micro uppercase tracking-label text-fg-muted">
              enrollments 24h
            </span>
            <div className="mt-0.5">
              <span
                className={`live-value font-mono text-base ${
                  (enrollments ?? 0) >= ENROLLMENT_WATCH ? 'text-warn' : 'text-fg'
                }`}
              >
                {formatInt(enrollments)}
              </span>
            </div>
          </div>

          <div>
            <span className="text-micro uppercase tracking-label text-fg-muted">
              reconexões/min
            </span>
            <div className="mt-0.5">
              <span
                className={`live-value font-mono text-base ${
                  reconnectTone === 'crit'
                    ? 'text-crit'
                    : reconnectTone === 'warn'
                      ? 'text-warn'
                      : 'text-fg'
                }`}
              >
                {formatDecimal(reconnect, 1)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Panel>
  )
}
